import { Injectable, inject } from '@angular/core';
import {
  IConversationSettings,
  AgentCardsAbstractService,
  ChatUserSettings,
  IAgentCard,
  ConversationMessagesDTO,
  TranscriptionsWhisper,
  IAgentResponseDTO,
  IAIModel,
} from '@dataclouder/ngx-agent-cards';
import { FiltersConfig, IFilterQueryResponse } from '@dataclouder/ngx-core';

import { ChatRole, Endpoints } from '../enums';
import { UserService } from './user.service';
import { AudioSpeedRating } from '../constants';
import { HttpService } from '../system/http.service';
import { ToastService } from '../system/toast.service';

export type AudioGenerated = { blobUrl: string; transcription: any };
export type TTSRequest = { text: string; voice: string; generateTranscription: boolean; speedRate: number; speed?: string; ssml?: string };

@Injectable({
  providedIn: 'root',
})
export class AgentCardsService implements AgentCardsAbstractService {
  public async generateMainImage(idCard: string): Promise<any> {
    const data = await this.httpService.getDataFromService(Endpoints.AgentCard.Generation.GenerateImage);

    return data;
    // throw new Error('Method not implemented.');
  }

  private httpService = inject(HttpService);
  private userService = inject(UserService);
  private toastService = inject(ToastService);

  public async findAgentCardByTitle(title: string): Promise<IAgentCard> {
    const filters: FiltersConfig = { filters: { title } };
    const response = await this.httpService.postDataToService<IFilterQueryResponse<IAgentCard>>(`${Endpoints.AgentCard.ConversationQuery}`, filters);
    return response.rows[0];
  }

  completeAgentCard(idCard: string): Promise<IAgentCard> {
    return this.httpService.getDataFromService(`${Endpoints.AgentCard.Generation.CompleteCard}/${idCard}`, 'primary');
  }

  public async findConversationByTitle(title: string): Promise<IAgentCard> {
    const filters: FiltersConfig = { filters: { title } };
    const response = await this.httpService.postDataToService<IFilterQueryResponse<IAgentCard>>(`${Endpoints.AgentCard.ConversationQuery}`, filters);
    return response.rows[0];
  }

  public async getAudioTranscriptions(audioBlob: Blob, metadata: unknown = null): Promise<TranscriptionsWhisper> {
    return await this.httpService.uploadAudioFile(`${Endpoints.Whisper.TranscribeBytes}`, audioBlob, metadata, 'node');
  }

  public async findFilteredAgentCards(paginator: FiltersConfig) {
    const user = this.userService.getUserSnapshot();

    paginator.filters['lang'] = user?.settings?.targetLanguage;
    if (!this.userService.isAdmin()) {
      paginator.filters['metaApp.isPublic'] = true;
    }
    const response = await this.httpService.postDataToService<IFilterQueryResponse<IAgentCard>>(`${Endpoints.AgentCard.UserCards}`, paginator);
    return response;
  }

  public async getListModels(provider: string): Promise<any> {
    const data = await this.httpService.getDataFromService(`${Endpoints.AgentCard.ListModelsNode}?provider=${provider}`, 'node');
    return data;
  }

  public async callChatCompletion(conversation: IConversationSettings | ConversationMessagesDTO): Promise<IAgentResponseDTO> {
    let messages = conversation.messages.map((m) => ({ content: m.content, role: m.role }));
    messages = messages.filter((m) => m.role != ChatRole.AssistantHelper);
    const conversationFiltered = { ...conversation, messages };

    // api/conversation/agent/chat python end point
    return await this.httpService.postDataToService<IAgentResponseDTO>(`${Endpoints.AgentCard.AgentChat}`, conversationFiltered, 'node');
  }

  public async callInstruction(prompt: string, model: IAIModel = {}): Promise<IAgentResponseDTO> {
    const messages = [{ content: prompt, role: ChatRole.User }];
    const conversationDto = { messages, model };

    return await this.httpService.postDataToService<IAgentResponseDTO>(`${Endpoints.AgentCard.AgentChat}`, conversationDto, 'python');
  }

  async translateConversation(currentLang: string, targetLang: string, idCard: string): Promise<ChatUserSettings> {
    const response = await this.httpService.postDataToService(`${Endpoints.AgentCard.TranslateConversation}`, { currentLang, targetLang, idCard }, 'python');

    return response;
  }

  async getConversationUserChatSettings(): Promise<ChatUserSettings> {
    const settings = (await this.userService.getUserSnapshot().settings.conversation) as ChatUserSettings;
    return settings;
  }

  async saveConversationSettings(settings: IAgentCard) {
    const response = await this.httpService.postDataToService('api/conversation-ai/settings', settings);
    return response;
  }

  async saveConversationCard(conversation: IAgentCard): Promise<IAgentCard> {
    if (conversation.id || conversation._id) {
      return await this.httpService.putDataToService(`${Endpoints.AgentCard.Conversation}/${conversation._id}`, conversation);
    } else {
      // Add default target language.
      conversation.lang = this.userService.getUserSnapshot().settings.targetLanguage;
      return await this.httpService.postDataToService(`${Endpoints.AgentCard.Conversation}`, conversation);
    }
  }

  async deleteConversationCard(id: string): Promise<any> {
    return await this.httpService.deleteDataFromService(`${Endpoints.AgentCard.Conversation}/${id}`);
  }

  async findConversationCardByID(id: string): Promise<IAgentCard> {
    return await this.httpService.getDataFromService(`${Endpoints.AgentCard.Conversation}/${id}`);
  }

  async filterConversationCards(filters: FiltersConfig): Promise<IFilterQueryResponse<IAgentCard>> {
    return await this.httpService.postDataToService(`${Endpoints.AgentCard.ConversationQuery}`, filters);
  }

  async getAllConversationCards(): Promise<any[]> {
    return await this.httpService.getDataFromService(`${Endpoints.AgentCard.Conversation}`);
  }

  // @Deprecated in favor of vertex ai. audio service
  public async getTextAudioFile(tts: TTSRequest): Promise<AudioGenerated> {
    const httpReq: any = await this.httpService.postFile(`${Endpoints.Vertex.tts}`, tts, 'node');
    const audioData = { blobUrl: null, transcription: null };

    const transcription = httpReq?.headers.get('transcription');

    if (transcription) {
      const data = JSON.parse(transcription);
      audioData.transcription = data;
    }

    const mp3 = window.URL.createObjectURL(httpReq.body);
    audioData.blobUrl = mp3;

    return audioData;
  }

  async saveConversationUserChatSettings(newSettings: any): Promise<any> {
    // TODO: deberia tener un metodo updateUserSettings() pero mientras tengo que pasar todo el objeto settings en vez de solo conversation
    const settings = { ...this.userService.getUserSnapshot().settings };
    const conversation: any = newSettings;

    // conversation.speed = AudioSpeedRating[conversation.speed ?? 3];

    settings.conversation = conversation;

    await this.userService.updateUser({ id: this.userService.getUserSnapshot().id, settings });

    this.toastService.success('Se actualizaron las configuraciones', 'Listo');
  }
}
